import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Star, ChevronLeft, CheckCircle, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminReviews() {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchReviews();
  }, [page]);

  const fetchReviews = async () => {
    try {
      const response = await api.get(`/admin/reviews?page=${page}&limit=20`);
      setReviews(response.data.reviews);
      setTotalPages(response.data.total_pages);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId, approve) => {
    try {
      await api.put(`/admin/reviews/${reviewId}/approve?is_approved=${approve}`);
      toast.success(`Avaliação ${approve ? 'aprovada' : 'reprovada'}`);
      fetchReviews();
    } catch (error) {
      toast.error('Erro ao atualizar avaliação');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-reviews">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Avaliações</h1>
          <p className="text-slate-600 mt-1">Moderar avaliações de pacientes</p>
        </div>

        <div className="space-y-4">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse h-32 bg-white rounded-2xl"></div>
            ))
          ) : reviews.length > 0 ? (
            reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={review.is_approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {review.is_approved ? 'Aprovada' : 'Pendente'}
                      </Badge>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={`star-${i}`} className={`w-4 h-4 ${i < review.overall_rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="font-medium text-slate-900">{review.patient_name || 'Paciente'}</p>
                    <p className="text-sm text-slate-600">Sobre: {review.doctor_name || 'Médico'}</p>
                    {review.comment && (
                      <p className="text-slate-600 mt-2">{review.comment}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      {format(new Date(review.created_at), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!review.is_approved && (
                      <Button
                        size="sm"
                        className="bg-emerald-600"
                        onClick={() => handleApprove(review.id, true)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Aprovar
                      </Button>
                    )}
                    {review.is_approved && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleApprove(review.id, false)}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reprovar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhuma avaliação encontrada</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-4 text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
